const PREFILL_STORAGE_PREFIX = 'editor-prefill:'
const PREFILL_MAX_AGE_MS = 30 * 60 * 1000

type StoredPrefillPayload = {
  content: string
  timestamp: number
}

const isSessionStorageAvailable = (): boolean => {
  try {
    const testKey = `${PREFILL_STORAGE_PREFIX}probe`
    window.sessionStorage.setItem(testKey, '1')
    window.sessionStorage.removeItem(testKey)
    return true
  } catch {
    return false
  }
}

const getStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null
  if (!isSessionStorageAvailable()) return null
  return window.sessionStorage
}

const cleanupExpiredPrefills = (storage: Storage, now: number) => {
  const keysToDelete: string[] = []
  for (let index = 0; index < storage.length; index++) {
    const key = storage.key(index)
    if (!key || !key.startsWith(PREFILL_STORAGE_PREFIX)) continue
    const rawValue = storage.getItem(key)
    if (!rawValue) {
      keysToDelete.push(key)
      continue
    }

    try {
      const parsed = JSON.parse(rawValue) as StoredPrefillPayload
      if (!parsed.timestamp || now - parsed.timestamp > PREFILL_MAX_AGE_MS) {
        keysToDelete.push(key)
      }
    } catch {
      keysToDelete.push(key)
    }
  }

  keysToDelete.forEach(key => storage.removeItem(key))
}

export const storeEditorPrefill = (content: string): string | null => {
  if (!content) return null
  const storage = getStorage()
  if (!storage) return null

  try {
    const token = crypto.randomUUID()
    const now = Date.now()
    const payload: StoredPrefillPayload = {
      content,
      timestamp: now
    }
    storage.setItem(`${PREFILL_STORAGE_PREFIX}${token}`, JSON.stringify(payload))
    cleanupExpiredPrefills(storage, now)
    return token
  } catch {
    return null
  }
}

export const consumeEditorPrefill = (token: string): string | null => {
  if (!token) return null
  const storage = getStorage()
  if (!storage) return null

  const key = `${PREFILL_STORAGE_PREFIX}${token}`
  const rawValue = storage.getItem(key)
  storage.removeItem(key)
  if (!rawValue) return null

  try {
    const parsed = JSON.parse(rawValue) as StoredPrefillPayload
    if (!parsed.content) return null
    if (!parsed.timestamp) return parsed.content
    if (Date.now() - parsed.timestamp > PREFILL_MAX_AGE_MS) return null
    return parsed.content
  } catch {
    return rawValue
  }
}
